import {
  WsHandlerOptions,
  WsEvent,
  WsMessageEvent,
  WsErrorEvent,
  WsCloseEvent,
  WsMessage,
  WsEventListenerMap,
  WsEventListenerObjectMap,
  WsEventMap,
  WsHandlerListenerMap,
} from './types'
export type {
  WsHandlerOptions,
  WsEvent,
  WsMessageEvent,
  WsErrorEvent,
  WsCloseEvent,
  WsMessage,
  WsEventListenerMap,
  WsEventListenerObjectMap,
  WsEventMap,
  WsHandlerListenerMap,
} from './types'

const DEFAULT_WS_HANDLER_OPTIONS: WsHandlerOptions = {
  reconnectionTimeout: 1000,
  timeoutInterval: 2000,
  maxreconnectionTimeout: 10000,
  maxRetries: Number.POSITIVE_INFINITY,
  reconnectionTimeoutDecay: 1.3,
  maxReconnectionDelay: 10000,
  minReconnectionDelay: 1000 + Math.random() * 4000,
  maxEnqueuedMessages: 1000,
  minUptime: 0,
} as const

export class WsHandler {
  public onopen: ((event: WsEvent) => Promise<void>) | null = null
  public onmessage: ((event: WsMessageEvent) => Promise<void>) | null = null
  public onerror: ((event: WsErrorEvent) => Promise<void>) | null = null
  public onclose: ((event: WsCloseEvent) => Promise<void>) | null = null

  private ws: WebSocket | null = null
  private url: string
  private messageQueue: WsMessage[] = []

  private listeners: WsHandlerListenerMap = {
    close: [],
    open: [],
    error: [],
    message: [],
  }

  private shouldReconnect = true
  private connectionLock = false
  private closeCalled = false

  private options: WsHandlerOptions = DEFAULT_WS_HANDLER_OPTIONS

  private retryCount = -1

  private reconnectionTimeoutHandle?: ReturnType<typeof setTimeout> | number
  private uptimeTimeoutHandle?: ReturnType<typeof setTimeout> | number

  constructor(url: string, options: Partial<WsHandlerOptions> = {}) {
    this.url = url
    this.setOptions(options)
  }

  private setOptions = (options: Partial<WsHandlerOptions>) => {
    for (const [key, value] of Object.entries(options)) {
      this.options[key as keyof WsHandlerOptions] = value
    }
  }

  private disconnect = async (code = 1000, reason?: string) => {
    console.debug(`WsHandler: diconnecting`)
    this.clearTimeouts()
    if (!this.ws) {
      return
    }
    this.removeListeners()
    try {
      this.ws.close(code, reason)
      await this.handleClose(new WsCloseEvent(code, reason, this))
    } catch (error) {
      console.debug(`WsHandler: error while calling 'disconnect': ${error}`)
    }
  }

  public reconnect = async (code?: number, reason?: string) => {
    this.shouldReconnect = true
    this.closeCalled = false
    this.retryCount = -1
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      await this._connect()
    } else {
      await this.disconnect(code, reason)
      await this._connect()
    }
  }

  public connect = async () => {
    if (this.ws) {
      return
    }
    return this._connect()
  }

  private _connect = async () => {
    if (this.connectionLock || !this.shouldReconnect) {
      return
    }
    this.connectionLock = true
    if (this.retryCount >= this.options.maxRetries) {
      console.debug(
        `WsHandler: max number of retries reached: ${this.retryCount} >= ${this.options.maxRetries}`,
      )
      return
    }

    this.retryCount++
    console.debug(`WsHandler: connecting, attempt #${this.retryCount}`)
    this.removeListeners()

    await this.wait()
    if (this.closeCalled) {
      return
    }
    this.ws = new WebSocket(this.url)
    this.connectionLock = false
    this.addListeners()
    this.reconnectionTimeoutHandle = setTimeout(
      () => this.handleReconnectionTimeout,
      this.options.reconnectionTimeout,
    )
  }

  public send = (data: WsMessage) => {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data)
    } else {
      if (this.messageQueue.length < this.options.maxEnqueuedMessages) {
        this.messageQueue.push(data)
      }
    }
  }

  private handleReconnectionTimeout = async () => {
    console.debug(`WsHandler: reconnection timeout`)
    await this.handleError(new WsErrorEvent(new Error('TIMEOUT'), this))
  }

  private handleClose = async (event: WsCloseEvent) => {
    console.debug(`WsHandler: close event`)
    this.clearTimeouts()

    if (this.shouldReconnect) {
      await this._connect()
    }

    if (this.onclose) {
      await this.onclose(event)
    }
    await Promise.all(
      this.listeners['close'].map((i) => this.callEventListener(event, i)),
    )
  }

  private handleOpen = async (event: WsEvent) => {
    console.debug(`WsHandler: open event`)

    clearTimeout(this.reconnectionTimeoutHandle)
    this.uptimeTimeoutHandle = setTimeout(() => {
      this.retryCount = 0
    }, this.options.minUptime)

    // send enqueued messages (messages sent before websocket open event)
    for (const message of this.messageQueue) {
      this.ws?.send(message)
    }
    this.messageQueue = []

    if (this.onopen) {
      await this.onopen(event)
    }
    await Promise.all(
      this.listeners['open'].map((i) => this.callEventListener(event, i)),
    )
  }

  public waitUntilOpen = async (ms = 1000, limitMs = 10000) => {
    const start = Date.now()
    return new Promise((resolve, reject) => {
      const handle = setInterval(() => {
        const delta = Date.now() - start
        if (delta > limitMs) {
          reject('Interval exceeded')
        }
        if (this.ws?.readyState === WebSocket.OPEN) {
          clearInterval(handle)
          resolve(undefined)
        }
      }, ms)
    })
  }

  private handleMessage = async (event: WsMessageEvent) => {
    if (this.onmessage) {
      await this.onmessage(event)
    }
    await Promise.all(
      this.listeners['message'].map((i) => this.callEventListener(event, i)),
    )
  }

  private handleError = async (event: WsErrorEvent) => {
    console.debug(
      `WsHandler: error event: ${event.message || JSON.stringify(event)}`,
    )
    await this.disconnect(
      undefined,
      event.message === 'TIMEOUT' ? 'timeout' : undefined,
    )

    if (this.onerror) {
      await this.onerror(event)
    }
    await Promise.all(
      this.listeners['error'].map((i) => this.callEventListener(event, i)),
    )
    await this._connect()
  }

  private removeListeners = () => {
    if (!this.ws) {
      return
    }
    this.ws.removeEventListener('open', this.handleOpen)
    this.ws.removeEventListener('close', this.handleClose)
    this.ws.removeEventListener('message', this.handleMessage)
    // @ts-ignore
    this.ws.removeEventListener('error', this.handleError)
  }

  private addListeners = () => {
    if (!this.ws) {
      return
    }
    this.ws.addEventListener('open', this.handleOpen)
    this.ws.addEventListener('close', this.handleClose)
    this.ws.addEventListener('message', this.handleMessage)
    // @ts-ignore
    this.ws.addEventListener('error', this.handleError)
  }

  private clearTimeouts = () => {
    clearTimeout(this.reconnectionTimeoutHandle)
    clearTimeout(this.uptimeTimeoutHandle)
  }

  public addEventListener = <T extends keyof WsEventMap = keyof WsEventMap>(
    type: T,
    listener: WsEventListenerMap[T],
  ): void => {
    if (this.listeners[type]) {
      console.debug(`WsHandler: listener added: ${type}`)
      // @ts-ignore
      this.listeners[type].push(listener)
    }
  }

  public removeEventListener<T extends keyof WsEventMap>(
    type: T,
    listener: WsEventListenerMap[T],
  ): void {
    if (this.listeners[type]) {
      // @ts-ignore
      this.listeners[type] = this.listeners[type].filter((l) => l !== listener)
    }
  }

  public dispatchEvent = async (event: WsEvent) => {
    const listeners = this.listeners[event.type as keyof WsEventListenerMap]
    if (listeners) {
      await Promise.all(listeners)
    }
    return true
  }

  private static isEventListenerObject<T extends keyof WsEventMap>(
    listener: WsEventListenerMap[T] | WsEventListenerObjectMap[T],
  ): listener is WsEventListenerObjectMap[T] {
    return 'handleEvent' in listener
  }

  private callEventListener = async <T extends keyof WsEventListenerMap>(
    event: WsEventMap[T],
    listener: WsEventListenerMap[T],
  ) => {
    if (WsHandler.isEventListenerObject(listener)) {
      //@ts-ignore
      listener.handleEvent(event)
    } else {
      //@ts-ignore
      await listener(event)
    }
  }

  private getReconnectionDelay = () => {
    const {
      reconnectionTimeoutDecay: decay,
      minReconnectionDelay: minDelay,
      maxReconnectionDelay: maxDelay,
    } = this.options
    let delay = 0
    if (this.retryCount > 0) {
      delay = minDelay * Math.pow(decay, this.retryCount - 1)
      if (delay > maxDelay) {
        delay = maxDelay
      }
    }
    return delay
  }

  private wait = (): Promise<void> => {
    return new Promise((resolve) => {
      setTimeout(resolve, this.getReconnectionDelay())
    })
  }

  public close = (code = 1000, reason?: string) => {
    this.closeCalled = true
    this.shouldReconnect = false
    this.clearTimeouts()
    if (!this.ws) {
      console.debug('WsHandler: close enqueued: no ws instance')
      return
    }
    if (this.ws.readyState === WebSocket.CLOSED) {
      console.debug('WsHandler: close: already closed')
      return
    }
    this.ws.close(code, reason)
  }
}
